/**
 * Composable that is registered/initialized on a global level, to allow us to systematically
 * manage their lifecycle, such as registering event hooks.
 *
 * NOTE: Due to a typescript limitation, you must call the class with the `initialized()`
 * method to properly initialize the state of the composable, since you cannot call child
 * methods in the constructor of an abstract class.
 */
export abstract class GlobalComposable<S extends object = never> {
  state: Ref<S> = undefined as any
  name: string
  protected logger: ReturnType<typeof useLogger>

  /**
   * @param name the name of this composable, for Nuxt state management
   * @param color an optional color to created colored logs for this composable
   */
  constructor(name: string, color?: string) {
    this.name = name
    if (isDefined(color) && !isTesting())
      this.logger = useLogger(this.name, color)
    else
      this.logger = globalThis.console
  }

  /** The default state set for this object's reactive state */
  defaultState = (): S => ({} as S)
  onCreate = (): void => {}
  onDestroy = (): void => {}

  /**
   * This method initializes any reactive state used by the composable object, since that
   * must happen within a vue context. The use of `useState` in nuxt ensures that this
   * object is not set to the default value every time, but only once then reused by
   * name. You should only export the `use` version of a composable in its declaration.
   * @returns the composable, initialized with reactive state
   */
  use() {
    this.state = useState<S>(`use-${this.name}`, this.defaultState)
    return this
  }

  clearState = () => {
    this.state.value = this.defaultState()
  }

  debug = (...vals: any[]) => this.logger.debug(...vals)
  log = (...vals: any[]) => this.logger.log(...vals)
  info = (...vals: any[]) => this.logger.info(...vals)
  warn = (...vals: any[]) => this.logger.warn(...vals)
  error = (...vals: any[]) => this.logger.error(...vals)
  trace = (...vals: any[]) => this.logger.trace(...vals)
  time = (...vals: any[]) => this.logger.time(...vals)
  timeLog = (...vals: any[]) => this.logger.timeLog(...vals)
  timeEnd = (...vals: any[]) => this.logger.timeEnd(...vals)
}
